import Image from "next/image";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import Text from "atoms/Text";
import SidebarListItems from "../molecules/SidebarListItems";
import SelectCompanySlideOver from './SelectCompanySlideOver';
import { setCompanyId, setCompanyTimeZone } from 'services/identity.service';
import { useEffect, useMemo, useState } from 'react';
import {
  getPlatformUserRole,
  isCustomerSuccess,
  isSuperAdminCampaignManager,
  roleDefaultPage,
} from '../lib/utils';
import SuperadminJourneysDropdown from "components/superadmin/SuperadminJourneysDropdown";
import {
  useGetCompaniesByUserIdQuery,
  useGetCompaniesQuery,
} from "utils/api/baseSlice";
import ErrorBoundary from "molecules/ErrorBoundaries/ErrorBoundary";
import { PRIVILEGE_ACTION, PRIVILEGE_SLUG } from "../constants";
import { useDispatch } from "react-redux";
import { setCanCreateJourney, setPrivileges } from "@/redux/privileges";
import {
  getSessionPrivilegesMap,
  hasPrivilege,
} from "services/permission.service";

const SuperAdminSidebar = ({
  mainLogoSrc = "/images/1ConnectLogo.svg",
  mainLogoWidth = 56,
  mainLogoHeight = 17,
  mainLogoAlt = "",
  userDetails
}) => {
  const myLoader = ({ src }) => src;
  const dispatch = useDispatch();
  const platformUserRole = getPlatformUserRole(userDetails?.roles);
  const privileges = useMemo(
    () => getSessionPrivilegesMap(userDetails),
    [userDetails]
  );
  const canCreateJourney = useMemo(
    () =>
      hasPrivilege(
        privileges,
        PRIVILEGE_SLUG.campaignTemplates,
        PRIVILEGE_ACTION.CREATE
      ),
    [privileges]
  );
  const customerSuccess = isCustomerSuccess(userDetails?.roles);
  const {
    data: userCompaniesDetails = [],
    isFetching: isFetchingUserCompanies,
  } = useGetCompaniesByUserIdQuery(userDetails?.id, {
    skip: customerSuccess,
    refetchOnMountOrArgChange: true,
  });
  const {
    data: allCompanies = {},
    isFetching: isFetchingAllCompanies,
  } = useGetCompaniesQuery({
    getSignedUrls: true,
  }, {
    skip: !customerSuccess,
    refetchOnMountOrArgChange: true,
  });
  const companiesData = customerSuccess
    ? (allCompanies?.rows || [])
    : (userCompaniesDetails?.company || []);
  const isFetchingCompanies = customerSuccess
    ? isFetchingAllCompanies
    : isFetchingUserCompanies;
  const router = useRouter();

  useEffect(() => {
    dispatch(setPrivileges(privileges));
    dispatch(setCanCreateJourney(canCreateJourney));
  }, [canCreateJourney, privileges]);

  const goToRoute = (str) => (e) => {
    e.preventDefault();
    router.push(str);
  };
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    if (isFetchingCompanies || !companiesData.length) return;
    const companiesWithSignedImages = companiesData.map((a) => ({ ...a }));
    companiesWithSignedImages.sort((a, b) =>
      a.companyName.toLowerCase().localeCompare(b.companyName.toLowerCase())
    );
    setCompanies(companiesWithSignedImages);
  }, [companiesData, isFetchingCompanies]);
  const [isSelectCompanySlideOver, setSelectCompanySlideOver] = useState(false);

  const onClickSuperAdmin = () => {
    setCompanyId(null);
    setCompanyTimeZone(null);
    setSelectCompanySlideOver(false);
    router.replace('/superadmin')
  }
  const onCompanySelect = async (company) => {
    setCompanyId(company.id);
    setCompanyTimeZone(company.timeZone);
    const roles = customerSuccess
      ? userDetails?.roles
      : companies.find((item) => item.id === company.id).userRoles.map((item) => item.Role);
    const routerPathObj = roleDefaultPage(roles, company);
    setSelectCompanySlideOver(false);
    router.push(routerPathObj);
  };


  return (
    <ErrorBoundary>
    <div className="min-h-screen w-64 px-3 pt-6 pb-12 fixed left-0 top-0 z-40 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 -z-10">
        <Image
          src="/images/Sidebar-green.webp"
          width={256}
          height={1080}
          alt="Sidebar background"
          priority
          style={{ objectFit: 'cover', width: '100%', height: '100%' }}
        />
      </div>
    
      <div className="flex justify-between items-center px-4 pb-6 mb-7 border-b border-gray-100 cursor-pointer"  onClick={() => setSelectCompanySlideOver(true)}>
        <div className="flex items-center">
          <div className="flex items-center mr-2.5">
            <Image id='img_tag_121'
              loader={myLoader}
              src="/images/1Connect-sidebar.webp"
              width={170}
              height={57}
              alt={"ANSR 1Connect"}
            />
            <div className="shrink-0 ml-2">
            <Image id='img_tag_122'
              src="/images/icons/downAngle-gray.svg"
              width="10"
              height="10"
              alt="Arrow"
            />
          </div>
          </div>
        </div>
      </div>
      <ul>
        { !isSuperAdminCampaignManager(userDetails) && (
          <>
            <SidebarListItems
              menuItem="Companies"
              menuIconNormalSrc="/images/icons/building-outline.svg"
              menuIconHoverSrc="/images/icons/building-outline-o.svg"
              menuIconAlt="Companies"
              menuActive={router.pathname.startsWith("/superadmin/companies")}
              onClick={goToRoute("/superadmin/companies")}
              href={"/superadmin/companies"}
            />
            <SidebarListItems
              menuItem="Users & Roles"
              menuIconNormalSrc="/images/icons/users.svg"
              menuIconHoverSrc="/images/icons/users-o.svg"
              menuIconAlt="Users & Roles"
              menuActive={router.pathname.startsWith("/superadmin/users")}
              onClick={goToRoute("/superadmin/users")}
            /> 
              <SidebarListItems
              menuItem="Analytics"
              menuIconNormalSrc="/images/icons/analytics.svg"
              menuIconHoverSrc="/images/icons/analytics-o.svg"
              menuIconAlt="Analytics"
              menuActive={router.pathname.startsWith("/superadmin/analytics")}
              onClick={goToRoute("/superadmin/analytics")}
            />
          </>
        )}
      
        <SuperadminJourneysDropdown />
      </ul>
      <div className="flex justify-center p-8 absolute bottom-0 z-[60]">
        <Text className="text-xxs shrink-0 mr-1 text-white"> Powered By</Text>
        <Image id='img_tag_123'
          loader={myLoader}
          src={mainLogoSrc}
          width={mainLogoWidth}
          height={mainLogoHeight}
          alt={mainLogoAlt}
        />
      </div>
      <SelectCompanySlideOver
        companyList={companies}
        isPlatformUser={true}
        platformUserRole={platformUserRole}
        onClickSuperAdmin={onClickSuperAdmin}
        isOpen={isSelectCompanySlideOver}
        onClose={() => setSelectCompanySlideOver(false)}
        onCompanySelect={onCompanySelect}
      />
    </div>
    </ErrorBoundary>
  );
};
export default SuperAdminSidebar;

SuperAdminSidebar.propTypes = {
  mainLogoSrc: PropTypes.string,
  mainLogoWidth: PropTypes.number,
  mainLogoHeight: PropTypes.number,
  mainLogoAlt: PropTypes.string,
};
